// features/council/meta.ts — static registry for the Council console.
import type { IconName } from '@/icons';
import type { CaseStatus, ModerationCategory } from '@/core/models/case';

export type CouncilSectionId =
  | 'overview' | 'case-queue' | 'human-review' | 'voting-board'
  | 'appeals' | 'audit-log' | 'feedback' | 'my-team' | 'ai-review' | 'settings';

export interface CouncilSection {
  id: CouncilSectionId;
  label: string;
  built: boolean;
}

export const SECTIONS: CouncilSection[] = [
  { id: 'overview',     label: 'Moderator Overview', built: true },
  { id: 'case-queue',   label: 'Case Queue',         built: true },
  { id: 'human-review', label: 'Human Review',       built: true },
  { id: 'voting-board', label: 'Voting Board',       built: true },
  { id: 'appeals',      label: 'Appeals',            built: true },
  { id: 'audit-log',    label: 'Audit Log',          built: true },
  { id: 'feedback',     label: 'Moderation Feedback', built: true },
  { id: 'my-team',      label: 'My Team',            built: false },
  { id: 'ai-review',    label: 'AI Review',          built: false },
  { id: 'settings',     label: 'Moderator Settings', built: false },
];

export const SECTION_IDS = new Set<string>(SECTIONS.map((s) => s.id));
export function isSectionId(id: string): id is CouncilSectionId {
  return SECTION_IDS.has(id);
}

export const SECTION_ICONS: Record<CouncilSectionId, IconName> = {
  'overview':     'bar-chart-3',
  'case-queue':   'list',
  'human-review': 'users',
  'voting-board': 'check',
  'appeals':      'flag',
  'audit-log':    'file-text',
  'feedback':     'megaphone',
  'my-team':      'user',
  'ai-review':    'info',
  'settings':     'settings',
};

// Moderator-facing labels. 'awaiting-head' is named plainly HERE, inside the
// Council console, because a moderator needs to know why they cannot vote yet.
//
// The MEMBER-FACING POST surfaces never use this wording: PostStatus and
// ProfileStatus render an 'awaiting-head' case as "Under Review", the label the
// published policy promises. That is a statement about those two components
// only — it is NOT a claim that members never see the string below. /council is
// IsAuthenticated, not staff-gated, so any signed-in member can open the console
// and read this table for every case in the queue.
export const STATUS_LABEL: Record<CaseStatus, string> = {
  'awaiting-head':   'Awaiting Head Moderator',
  'under-review':    'Under Review',
  'decided':         'Decided',
  'appeal-review':   'Appeal Review',
  'appeal-resolved': 'Appeal Resolved',
};

// Pill tone per status.
export type StatusTone = 'amber' | 'green' | 'violet' | 'cyan';
export const STATUS_TONE: Record<CaseStatus, StatusTone> = {
  'awaiting-head':   'amber',
  'under-review':    'amber',
  'decided':         'green',
  'appeal-review':   'violet',
  'appeal-resolved': 'cyan',
};

// Category badge tone (per the five categories) — CSS color literals (accepted local exception).
export const CATEGORY_TONE: Record<ModerationCategory, string> = {
  'threats':           '#ff6b6b',
  'terrorism':         '#ff8d8d',
  'scams':             '#ff5ba6',
  'fraud':             '#ffb13b',
  'coordinated-abuse': '#c79bff',
};
